<template>
  <v-app>
    <app-top-bar :class="hasBackgroundImage ? '' : 'bg-background'" :path-return="pathPage">{{ $t("bikePossession.pageTitle") }}</app-top-bar>

    <v-main :style="mainStyle" :class="hasBackgroundImage ? '' : 'bg-background'">
      <v-container v-if="!showComplete" class="fill-height flex-column">
        <div class="align-self-start">
          <h1 class="page-title mb-6 text-on-surface-variant">{{ $t("bikePossession.mainTitle") }}</h1>
          <p v-if="!initializing" class="body-1 text-on-surface-variant">
            {{ api.eligibleForDeferredInspection ? $t("bikePossession.deferrableDescription") : $t("bikePossession.description") }}
          </p>
        </div>

        <div v-if="initializing" class="w-100 my-auto d-flex justify-center">
          <v-progress-circular indeterminate color="primary" />
        </div>
        <div v-else class="w-100 my-auto">
          <app-button
            block
            class="my-3"
            color="primary"
            style="height: 48px"
            variant="outlined"
            :disabled="isAdvancing"
            :loading="isAdvancing"
            @click="advance"
          >
            {{ api.eligibleForDeferredInspection ? $t("bikePossession.inspectNowButton") : $t("bikePossession.yesButton") }}
          </app-button>
          <app-button
            block
            class="my-3"
            color="primary"
            style="height: 48px"
            variant="outlined"
            :disabled="isAdvancing"
            :loading="isAdvancing"
            @click="onSecondaryAction"
          >
            {{ api.eligibleForDeferredInspection ? $t("bikePossession.sendLaterButton") : $t("bikePossession.noButton") }}
          </app-button>
        </div>
      </v-container>
      <v-container v-if="showComplete">
        <phase-completed />
      </v-container>
    </v-main>

    <app-footer v-if="showComplete">
      <continue-footer-button :loading="isAdvancing" @click="advanceFlow" />
    </app-footer>
  </v-app>
</template>

<script setup lang="ts">
import {computed, onMounted, ref} from "vue";
import {usePageBackground} from "@/composables/usePageBackground";
import {useStepFlow} from "@/composables/useStepFlow";
import {useApiStore} from "@/stores/api.ts";
import {showToast} from "@/utils/toast";
import {useI18n} from "vue-i18n";
import AppTopBar from "@/components/AppTopBar/AppTopBar.vue";
import AppButton from "@/components/AppButton/AppButton.vue";
import PhaseCompleted from "@/components/PhaseCompleted/PhaseCompleted.vue";
import ContinueFooterButton from "@/components/ContinueFooterButton/ContinueFooterButton.vue";
import AppFooter from "@/components/AppFooter/AppFooter.vue";

const { t } = useI18n();
const api = useApiStore();
const { mainStyle, hasBackgroundImage } = usePageBackground();
const pathPage = '/bike-possession';

const { isAdvancing, showComplete, advance: advanceFlow } = useStepFlow(pathPage, () => isComplete.value);

const isComplete = computed<boolean>(() => api.isItemComplete("bikePossession"));

const initializing = ref(true);


onMounted(async () => {
  try {
    await api.getData();
  } finally {
    initializing.value = false;
  }
});

function onSecondaryAction() {
  if (api.eligibleForDeferredInspection) {
    confirmDefer();
  } else {
    noBikePossession();
  }
}

function noBikePossession() {
  showToast.warning({
    title: t("bikePossession.toastTitle"),
    description: t("bikePossession.toastDescription"),
  });
}

async function confirmDefer() {
  try {
    await advanceFlow(async () => {
      await api.deferInspection();
    });
  } catch (err) {
    showToast.warning({
      title: t("bikePossession.deferredErrorTitle"),
      description: t("bikePossession.deferredErrorDescription"),
    });
  }
}

async function advance() {
  await advanceFlow(async () => {
    if (api.inspectionDeferred) {
      await api.cancelDeferInspection();
    }
  });
}
</script>

<style>
.button {
  height: 50px;
}
</style>
